import React, { useEffect, useRef, useState } from 'react';
import { owner, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Plus, Trash2, X, Camera, Loader2, Gavel, Clock, Users } from 'lucide-react';
import AppImage from '../components/AppImage';

const BLANK = { title:'', startingBid: 100, currentBid: 100, minIncrement: 10, endsIn: '24h', estimatedValue: '', businessId: '', image: '', pickupInstructions: '', testAuction: false };

export default function ManageAuctions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => owner.myAuctions().then(setItems).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const pickImage = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const res = await filesApi.upload(f, 'business');
      setF('image', res.url);
      toast({ title: 'Photo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setUploading(false); e.target.value=''; }
  };

  const save = async () => {
    if (!form.title) { toast({ title: 'Add a title' }); return; }
    if ((form.pickupInstructions || '').trim().length < 10) { toast({ title: 'Delivery or pickup instructions required', description: 'At least 10 characters.' }); return; }
    setSaving(true);
    try {
      const item = await owner.createAuction(form);
      setItems(prev => [item, ...prev]);
      setOpen(false); setForm(BLANK);
      toast({ title: 'Auction item added!', description: '100% of proceeds fund the community.' });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this auction item?')) return;
    await owner.deleteAuction(id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">My Auction Items</h1>
          <p className="text-gray-400 text-sm">List items to raise funds for the community.</p>
        </div>
        <button onClick={() => setOpen(true)} className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-2">
          <Plus size={16}/> Add Auction Item
        </button>
      </div>

      <div className="pbb-card p-4 bg-green-600/5 border-green-600/30">
        <div className="flex items-start gap-3">
          <Gavel className="text-green-400 mt-0.5" size={18}/>
          <div className="text-sm text-gray-300">
            <b className="text-green-400">100% of proceeds</b> from your auction items are split evenly between the Community Fund and Young Leadership Fund. Thank you for building the movement.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>
      ) : items.length === 0 ? (
        <div className="pbb-card p-10 text-center">
          <Gavel className="mx-auto text-green-400" size={32}/>
          <div className="mt-3 font-semibold">No auction items yet</div>
          <p className="text-sm text-gray-400 mt-1">Add an item to start raising funds today.</p>
          <button onClick={() => setOpen(true)} className="pbb-btn-green mt-5 max-w-xs mx-auto">Add your first item</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(it => (
            <div key={it.id} className="pbb-card overflow-hidden flex flex-col h-full">
              <AppImage src={filesApi.urlFor(it.image)} alt={it.title} rounded="none" fallback={<Gavel size={32}/>} />
              <div className="p-4 flex flex-col flex-1">
                <div className="font-semibold text-sm">{it.title}</div>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                  <span className="inline-flex items-center gap-1"><Clock size={12}/> {it.endsIn}</span>
                  <span className="inline-flex items-center gap-1"><Users size={12}/> {it.bids} bids</span>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-500">Current bid</div>
                    <div className="text-xl font-bold text-violet-300">{(it.currentBid || 0).toLocaleString()} pts</div>
                    {it.estimatedValue ? <div className="text-[10px] text-gray-500">~${it.estimatedValue}</div> : null}
                  </div>
                  <button onClick={() => remove(it.id)} className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-red-500 hover:text-red-400 text-xs inline-flex items-center gap-1"><Trash2 size={12}/> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="pbb-card w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-lg">Add Auction Item</div>
              <button onClick={() => setOpen(false)} className="text-gray-400"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#0d1f14] flex items-center justify-center relative">
                  {form.image ? <img src={filesApi.urlFor(form.image)} alt="" className="w-full h-full object-cover"/> : <Gavel className="text-gray-600" size={28}/>}
                  {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-white"/></div>}
                </div>
                <div>
                  <button onClick={pickImage} disabled={uploading} className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-xs inline-flex items-center gap-1">
                    <Camera size={12}/> {form.image ? 'Change photo' : 'Upload photo'}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile}/>
                </div>
              </div>
              <input className="pbb-input" placeholder="Item title *" value={form.title} onChange={e=>setF('title', e.target.value)}/>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Starting bid (pts)</label>
                  <input className="pbb-input" type="number" min="1" value={form.startingBid} onChange={e=>{ const v=parseInt(e.target.value||'0',10); setF('startingBid', v); setF('currentBid', v); }}/>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Min increment (pts)</label>
                  <input className="pbb-input" type="number" min="1" value={form.minIncrement} onChange={e=>setF('minIncrement', parseInt(e.target.value||'0',10))}/>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Estimated dollar value (optional, display only)</label>
                <input className="pbb-input" type="number" min="0" step="0.01" placeholder="$ e.g. 250" value={form.estimatedValue} onChange={e=>setF('estimatedValue', parseFloat(e.target.value||'0'))}/>
              </div>
              <input className="pbb-input" placeholder="Duration (e.g. 24h, 3d)" value={form.endsIn} onChange={e=>setF('endsIn', e.target.value)}/>
              <div>
                <label className="text-xs text-gray-300 mb-1 block">Delivery or pickup instructions <span className="text-red-400">*</span></label>
                <textarea className="pbb-input" rows={3} maxLength={1000} data-testid="auction-pickup-instructions"
                  placeholder="e.g. Pick up at our shop, 123 Main St, Mon–Fri 10–6. Or: we ship within 2 days."
                  value={form.pickupInstructions} onChange={e=>setF('pickupInstructions', e.target.value)}/>
                <p className="text-[11px] text-gray-400 mt-1">Sent to the winner by email and text after they pay.</p>
              </div>
              <label className="flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 cursor-pointer" data-testid="auction-test-wrap">
                <input type="checkbox" checked={!!form.testAuction} onChange={e=>setF('testAuction', e.target.checked)} className="mt-0.5 accent-yellow-500" data-testid="auction-test-checkbox"/>
                <span className="text-sm text-gray-100">Test Auction<span className="block text-[11px] text-gray-300">Ends 3 minutes from now, ignoring the duration above.</span></span>
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="flex-1 px-3 py-2.5 rounded-lg border border-[#2a2a2a] text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 pbb-btn-green disabled:opacity-70 inline-flex items-center justify-center gap-2">
                {saving && <Loader2 className="animate-spin" size={14}/>} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
